// src/components/CompanySwitcher.tsx
//
// Lets a super admin choose which company they are working on.
//
// It lives in the header rather than in Settings on purpose. The risk this
// feature introduces is not picking the wrong company - that is one click to
// undo - it is forgetting which one you are in three pages later and uploading
// an asset into somebody else's library. So the current company is on screen
// permanently, and when it is not your own it does not look like it is.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, ChevronDown, Check, Loader2, AlertCircle, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authApi, CompanySwitcherEntry } from '../services/authApi';
import { CompanyType } from '../types/companies';

// Below this many companies a filter box is clutter; above it, scanning the
// list stops being possible. Pinage has two today and will grow past this.
const FILTER_THRESHOLD = 8;

const CompanySwitcher: React.FC = () => {
  const { user, actingAs, startActingAs, stopActing } = useAuth();

  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<CompanySwitcherEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);

  const currentCompanyId = actingAs?.companyId ?? user?.companyId;
  const currentCompanyName = actingAs?.companyName ?? user?.companyName;

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await authApi.getCompanySwitcherEntries());
    } catch (err) {
      setError('Could not load companies');
      console.error('Error loading company switcher entries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetched when the menu opens rather than on mount, and again on every open.
  // The attention counts are the reason to look at this list at all, and a count
  // cached since the page loaded is worse than no count - it would send somebody
  // to a customer whose devices recovered twenty minutes ago.
  useEffect(() => {
    if (open) loadEntries();
  }, [open, loadEntries]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Only super admins have anywhere to switch to.
  if (user?.role !== 'SuperAdmin') return null;

  const handleChoose = async (entry: CompanySwitcherEntry) => {
    if (entry.companyId === currentCompanyId) {
      setOpen(false);
      return;
    }

    setSwitchingTo(entry.companyId);
    setError(null);
    try {
      // Going home has its own endpoint, which closes the audit row. act-as
      // would answer correctly too, but silently leave the session open.
      if (entry.companyId === user?.companyId) {
        await stopActing();
      } else {
        // Going straight from one customer to another means coming home first.
        //
        // The API refuses to mint an acting token from an acting token, because
        // allowing it would restart the hour on every switch and remove the only
        // thing that ever ends a forgotten session. That guard is right, but it
        // would surface here as "Return to your own company before working on a
        // different one" in response to a perfectly reasonable click. Two
        // requests instead of one, and the person switching sees a switch.
        if (actingAs) {
          await stopActing();
        }
        await startActingAs(entry.companyId);
      }
      setOpen(false);
      setFilter('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not switch company');
    } finally {
      setSwitchingTo(null);
    }
  };

  const visible = filter.trim()
    ? entries.filter(e => e.companyName.toLowerCase().includes(filter.trim().toLowerCase()))
    : entries;

  const statusDot = (entry: CompanySwitcherEntry) => {
    if (entry.deviceCount === 0) return 'bg-gray-300';
    return entry.devicesNeedingAttention > 0 ? 'bg-red-500' : 'bg-green-500';
  };

  const describeDevices = (entry: CompanySwitcherEntry): string => {
    if (entry.deviceCount === 0) return 'no devices';
    const devices = `${entry.deviceCount} device${entry.deviceCount === 1 ? '' : 's'}`;
    return entry.devicesNeedingAttention > 0
      ? `${devices} · ${entry.devicesNeedingAttention} need${entry.devicesNeedingAttention === 1 ? 's' : ''} attention`
      : `${devices} · all healthy`;
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 ${
          actingAs
            ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Building2 className="h-4 w-4" />
        <span className="max-w-[12rem] truncate">{currentCompanyName}</span>
        <ChevronDown className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-80 rounded-md border border-gray-200 bg-white shadow-lg"
        >
          <div className="border-b border-gray-100 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Work on
          </div>

          {entries.length > FILTER_THRESHOLD && (
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder="Find a company"
                  className="w-full rounded border border-gray-200 py-1.5 pl-8 pr-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {loading && entries.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-4 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading companies...
            </div>
          )}

          {!loading && visible.length === 0 && !error && (
            <div className="px-4 py-4 text-sm text-gray-500">
              {filter ? 'No company matches that' : 'No companies found'}
            </div>
          )}

          <div className="max-h-80 overflow-y-auto py-1">
            {visible.map(entry => {
              const isCurrent = entry.companyId === currentCompanyId;
              const isHome = entry.companyId === user?.companyId;
              const isSwitching = switchingTo === entry.companyId;

              return (
                <button
                  key={entry.companyId}
                  type="button"
                  role="menuitem"
                  disabled={switchingTo !== null}
                  onClick={() => handleChoose(entry)}
                  className={`flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 disabled:opacity-60 ${
                    isCurrent ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                    {isSwitching ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    ) : isCurrent ? (
                      <Check className="h-4 w-4 text-blue-600" />
                    ) : (
                      <span className={`h-2 w-2 rounded-full ${statusDot(entry)}`} />
                    )}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className={`truncate text-sm ${isCurrent ? 'text-blue-900' : 'text-gray-900'}`}>
                        {entry.companyName}
                      </span>
                      {/* An inactive customer is still worth working on - their
                          Pis are usually the ones being set up - but it should
                          be obvious that is what you are looking at. */}
                      {!entry.isActive && entry.companyType === CompanyType.Customer && (
                        <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                          not active
                        </span>
                      )}
                    </span>
                    <span className={`block truncate text-xs ${isCurrent ? 'text-blue-700' : 'text-gray-500'}`}>
                      {isHome ? 'your own company · ' : ''}
                      {describeDevices(entry)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanySwitcher;
